// File: src/components/FilterOptions.js
import React, { useState, useEffect } from 'react';
import { Collapse, Checkbox, Slider, Rate, Button, Typography, Space, Tag, Divider, Spin } from 'antd';
import { fetchAllBrands } from '../services/shopService';
// import './FilterOptions.css'; // Remove CSS import

const { Panel } = Collapse;
const { Title, Text } = Typography;

// Available brands - keep this list or fetch dynamically if needed
// const availableBrands = [
//     'Epok', 'General', 'Göteborgs Rapé', 'Lyft', 'Nick & Johnny',
//     'Siberia', 'Skruf', 'Thunder', 'XQS', 'Zyn'
// ];

const FilterOptions = ({ filters, onChange, shopCount }) => {
    // State für die dynamische Markenliste und Ladezustand
    const [dynamicBrands, setDynamicBrands] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(true);

    // Effekt zum Laden der Marken beim Mounten der Komponente
    useEffect(() => {
        const loadBrands = async () => {
            setLoadingBrands(true);
            try {
                const brands = await fetchAllBrands();
                setDynamicBrands(brands);
            } catch (error) {
                // Fehlerbehandlung bleibt im Service, hier nur Logging falls nötig
                console.error('Fehler beim Setzen der Marken im FilterOptions Component.');
            } finally {
                setLoadingBrands(false);
            }
        };
        loadBrands();
    }, []); // Leeres Abhängigkeitsarray, damit der Effekt nur einmal beim Mounten läuft

    // Filter handlers - adapt slightly for Ant Design components if needed
    const handleOpenChange = (e) => {
        onChange({ open: e.target.checked });
    };

    const handleBrandChange = (checkedValues) => {
        // Checkbox.Group onChange passes an array of checked values
        onChange({ brands: checkedValues });
    };

    const handleDistanceChange = (value) => {
        // Slider onChange passes the value directly
        onChange({ distance: value });
    };

    const handleRatingChange = (value) => {
        // Rate onChange passes the rating value (0 if cleared)
        onChange({ minRating: value || 0 }); // Set to 0 if Rate is cleared
    };

    const handleShopTypeChange = (type, checked) => {
        const shopTypesUpdate = { ...filters.shopTypes };
        shopTypesUpdate[type] = checked;
        onChange({ shopTypes: shopTypesUpdate });
    };

    const handleResetFilters = () => {
        onChange({
            open: false,
            distance: 10, // Default distance
            brands: [],
            minRating: 0,
            shopTypes: { premium: false, verified: false }
        });
    };

    // Check if any filters are active to enable/disable reset button
    const hasActiveFilters =
        filters.open ||
        filters.distance !== 10 ||
        filters.brands.length > 0 ||
        filters.minRating > 0 ||
        filters.shopTypes?.premium ||
        filters.shopTypes?.verified;

    // Helper to format Slider tooltip
    const distanceFormatter = (value) => `${value} km`;

    return (
        <div className="filter-options-antd"> {/* Add a wrapper class if needed for spacing/styling */} 
            <Space direction="vertical" style={{ width: '100%' }}>
                <Title level={4}>Filter</Title>
                <Text type="secondary">{shopCount} Ergebnisse gefunden</Text>
                <Divider style={{ margin: '12px 0' }}/>

                <Collapse defaultActiveKey={['1', '2', '3', '4', '5']} ghost expandIconPosition="end">
                    {/* Panel 1: Öffnungszeiten */}
                    <Panel header="Öffnungszeiten" key="1">
                        <Checkbox
                            checked={filters.open}
                            onChange={handleOpenChange}
                        >
                            Jetzt geöffnet
                        </Checkbox>
                    </Panel>

                    {/* Panel 2: Entfernung */}
                    <Panel header="Entfernung" key="2">
                        <Slider
                            min={1}
                            max={50} // Adjust max distance if needed
                            step={1}
                            value={filters.distance}
                            onChange={handleDistanceChange}
                            tooltip={{ formatter: distanceFormatter }}
                            marks={{ 1: '1km', 10: '10km', 25: '25km', 50: '50km' }}
                        />
                        <Text style={{ display: 'block', textAlign: 'center', marginTop: '5px' }}>
                            Max. {filters.distance} km entfernt
                        </Text>
                    </Panel>

                    {/* Panel 3: Bewertung */}
                    <Panel header="Mindestbewertung" key="3">
                        <Rate
                            allowHalf={false} // Only full stars
                            value={filters.minRating}
                            onChange={handleRatingChange}
                            allowClear={true} // Allow deselecting by clicking the current rating
                            style={{ fontSize: 24 }} // Adjust star size if needed
                        />
                        {filters.minRating > 0 && (
                            <Text type="secondary" style={{ marginLeft: '10px' }}>
                                ({filters.minRating} Sterne oder mehr)
                            </Text>
                        )}
                    </Panel>

                    {/* Panel 4: Marken */}
                    <Panel header={`Marken ${filters.brands.length > 0 ? `(${filters.brands.length})` : ''}`} key="4">
                        {loadingBrands ? (
                            <Spin size="small" />
                        ) : dynamicBrands.length > 0 ? (
                            <Checkbox.Group
                                options={dynamicBrands.map(brand => ({ label: brand, value: brand }))}
                                value={filters.brands}
                                onChange={handleBrandChange}
                                style={{ display: 'flex', flexDirection: 'column' }}
                            />
                        ) : (
                            <Text type="secondary">Keine Marken verfügbar.</Text>
                        )}
                    </Panel>

                    {/* Panel 5: Shop-Typ */}
                    <Panel header="Shop-Typ" key="5">
                        <Space direction="vertical">
                            {/* <Checkbox
                                checked={filters.shopTypes?.premium || false}
                                onChange={(e) => handleShopTypeChange('premium', e.target.checked)}
                            >
                                Premium <Tag color="gold">PRO</Tag>
                            </Checkbox> // Premium-Filter entfernt */}
                            <Checkbox
                                checked={filters.shopTypes?.verified || false}
                                onChange={(e) => handleShopTypeChange('verified', e.target.checked)}
                            >
                                Verifiziert <Tag color="success">✓</Tag>
                            </Checkbox>
                         </Space>
                    </Panel>
                </Collapse>

                <Divider style={{ margin: '12px 0' }}/>

                <Button
                    onClick={handleResetFilters}
                    disabled={!hasActiveFilters}
                    block // Make button full width
                >
                    Alle Filter zurücksetzen
                </Button>
            </Space>
        </div>
    );
};

export default FilterOptions;